<template>
  <FlashNotifications ref="flash" />

  <!-- Desktop -->
  <div class="hidden sm:flex sm:flex-col bg-gray-50 dark:bg-[#0d1117]" style="height: calc(100vh - 57px)">

    <div class="flex-shrink-0 bg-white dark:bg-[#1C2128] border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center justify-between px-6 py-3.5 gap-4">
        <div>
          <h1 class="text-xl font-bold text-gray-900 dark:text-white leading-none">Career Jobs</h1>
          <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Roles listed at /careers. A live role drops off that list once its deadline passes, but keeps its own page.
          </p>
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <a href="/careers" target="_blank" rel="noopener"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors">
            View public page
          </a>
          <button type="button" @click="openNew"
            class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
            </svg>
            New role
          </button>
        </div>
      </div>

      <div class="flex items-center gap-2.5 px-6 pb-3.5">
        <div class="relative flex-1 max-w-xs">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"/>
          </svg>
          <input v-model="search" @input="onSearchInput" placeholder="Search title, location or summary…"
            class="pl-9 pr-3 py-2 w-full text-sm bg-gray-50 dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" />
        </div>

        <div class="flex items-center gap-1.5">
          <button v-for="option in STATUS_FILTERS" :key="option.value" type="button" @click="setStatus(option.value)"
            class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg transition-colors"
            :class="status === option.value
              ? 'bg-blue-600 text-white'
              : 'bg-gray-50 dark:bg-[#0d1117] text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'">
            {{ option.label }}
            <span class="min-w-[20px] h-5 inline-flex items-center justify-center rounded-full text-[11px] font-bold px-1"
              :class="status === option.value ? 'bg-white/20 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'">
              {{ countFor(option.value) }}
            </span>
          </button>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto px-6 py-5">

      <div v-if="loading" class="animate-pulse rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1C2128] overflow-hidden">
        <div class="h-11 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-[#161b22]"></div>
        <div v-for="n in skeletonRows" :key="n" class="flex items-center gap-4 px-5 py-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
          <div class="flex-1 h-5 bg-gray-100 dark:bg-gray-800 rounded"></div>
          <div class="w-24 h-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div class="w-28 h-5 bg-gray-100 dark:bg-gray-800 rounded"></div>
          <div class="w-24 h-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>

      <div v-else-if="error" class="flex flex-col items-center justify-center py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
          <svg class="w-7 h-7 text-red-500" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">Something went wrong</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">{{ error }}</p>
        <button @click="retry" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors">Try again</button>
      </div>

      <div v-else-if="records.length === 0" class="flex flex-col items-center justify-center py-32 text-center px-4">
        <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
          <svg class="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" stroke-width="1.6" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M20.25 14.15v4.07a2.25 2.25 0 0 1-2.25 2.25H5.999a2.25 2.25 0 0 1-2.25-2.25v-4.07M8.25 9.75V4.875c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125V9.75"/>
          </svg>
        </div>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">{{ hasFilters ? 'No roles match' : 'No roles posted yet' }}</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-5">
          {{ hasFilters ? 'Try a different search or filter.' : 'Post one and it appears on /careers as soon as you publish it.' }}
        </p>
        <button v-if="!hasFilters" @click="openNew" class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors">New role</button>
      </div>

      <div v-else class="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-[#1C2128] overflow-hidden">
        <table class="w-full text-left">
          <thead class="bg-gray-50 dark:bg-[#161b22] border-b border-gray-100 dark:border-gray-700">
            <tr class="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              <th class="px-5 py-3">Role</th>
              <th class="px-5 py-3 w-28">Type</th>
              <th class="px-5 py-3 w-40">Deadline</th>
              <th class="px-5 py-3 w-32">Applicants</th>
              <th class="px-5 py-3 w-32">Status</th>
              <th class="px-5 py-3 w-52 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-for="record in records" :key="record.id" class="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors">
              <td class="px-5 py-4">
                <button type="button" @click="openEdit(record)" class="text-sm font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-left">
                  {{ record.title }}
                </button>
                <p class="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{{ record.location }}</p>
              </td>
              <td class="px-5 py-4">
                <span class="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  {{ record.category_label }}
                </span>
              </td>
              <td class="px-5 py-4 text-sm">
                <template v-if="record.deadline">
                  <span :class="record.closed ? 'font-semibold text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'">
                    {{ formatDate(record.deadline) }}
                  </span>
                  <span v-if="record.days_remaining !== null && record.days_remaining <= 14"
                    class="block text-[11px] text-amber-600 dark:text-amber-400">
                    {{ record.days_remaining === 0 ? 'Closes today' : `${record.days_remaining} days left` }}
                  </span>
                </template>
                <span v-else class="text-gray-300 dark:text-gray-600">No deadline</span>
              </td>
              <td class="px-5 py-4">
                <button v-if="record.applicants_count > 0" type="button" @click="openApplicants(record)"
                  class="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline">
                  {{ record.applicants_count }} {{ record.applicants_count === 1 ? 'applicant' : 'applicants' }}
                </button>
                <span v-else class="text-sm text-gray-300 dark:text-gray-600">None yet</span>
              </td>
              <td class="px-5 py-4">
                <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold" :class="statusClass(record.status)">
                  <span class="w-1.5 h-1.5 rounded-full" :class="statusDot(record.status)"></span>
                  {{ statusLabel(record.status) }}
                </span>
              </td>
              <td class="px-5 py-4">
                <div class="flex items-center justify-end gap-1.5">
                  <button type="button" @click="openEdit(record)"
                    class="px-2.5 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">Edit</button>
                  <button v-if="!record.publish" type="button" @click="publish(record)" :disabled="working === record.id"
                    class="px-2.5 py-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors disabled:opacity-50">Publish</button>
                  <button v-else type="button" @click="unpublish(record)" :disabled="working === record.id"
                    class="px-2.5 py-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors disabled:opacity-50">Unpublish</button>
                  <button v-if="record.applicants_count === 0" type="button" @click="askDelete(record)" :disabled="working === record.id"
                    class="px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
                    :class="pendingDelete === record.id ? 'bg-red-600 text-white hover:bg-red-700' : 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'">
                    {{ pendingDelete === record.id ? 'Confirm' : 'Delete' }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="!loading && !error && page.total_pages > 1" class="flex items-center justify-between mt-4">
        <p class="text-xs text-gray-400 dark:text-gray-500">
          Page {{ page.current_page }} of {{ page.total_pages }} · {{ page.total_records }} roles
        </p>
        <div class="flex items-center gap-1.5">
          <button type="button" :disabled="page.current_page <= 1" @click="goToPage(page.current_page - 1)"
            class="px-3 py-1.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">Previous</button>
          <button type="button" :disabled="page.current_page >= page.total_pages" @click="goToPage(page.current_page + 1)"
            class="px-3 py-1.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-[#1C2128] border border-gray-200 dark:border-gray-700 rounded-lg disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">Next</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile -->
  <div class="sm:hidden bg-gray-50 dark:bg-[#0d1117] min-h-screen">
    <div class="sticky top-0 z-10 bg-white dark:bg-[#1C2128] border-b border-gray-100 dark:border-gray-700">
      <div class="flex items-center justify-between px-4 h-14">
        <h1 class="text-[22px] font-bold text-gray-900 dark:text-white tracking-tight leading-none">Career Jobs</h1>
        <button type="button" @click="openNew" class="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full">New</button>
      </div>
    </div>

    <div class="pb-20">
      <div v-if="loading" class="animate-pulse px-4 pt-6 space-y-3">
        <div v-for="n in skeletonRows" :key="n" class="bg-white dark:bg-[#1C2128] rounded-2xl p-4 space-y-2">
          <div class="w-3/4 h-5 bg-gray-100 dark:bg-gray-800 rounded"></div>
          <div class="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>

      <template v-else-if="!error">
        <p class="px-4 pt-6 pb-2 text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
          {{ page.total_records }} roles
        </p>
        <div class="bg-white dark:bg-[#1C2128] rounded-2xl mx-4 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
          <button v-for="record in records" :key="record.id" type="button" @click="openEdit(record)" class="w-full text-left px-4 py-3.5">
            <div class="flex items-center gap-2">
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold" :class="statusClass(record.status)">
                {{ statusLabel(record.status) }}
              </span>
              <span class="text-xs text-gray-400 dark:text-gray-500">{{ record.category_label }}</span>
            </div>
            <p class="mt-1 text-sm font-semibold text-gray-900 dark:text-white">{{ record.title }}</p>
            <p class="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{{ record.location }}</p>
          </button>
        </div>
      </template>
    </div>
  </div>

  <CareerJobFormSlideOver
    :open="formOpen"
    :saving="saving"
    :error="formError"
    :career-job="editing"
    @close="formOpen = false"
    @submit="onFormSubmit" />

  <CareerJobApplicantsSlideOver
    :open="applicantsOpen"
    :loading="applicantsLoading"
    :error="applicantsError"
    :job-title="applicantsJobTitle"
    :records="applicants"
    @close="applicantsOpen = false" />
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAdminCareerJobsStore, STATUS_FILTERS } from 'stores/admin_career_jobs.js'
import { getCachedCareerJobsCount, setCachedCareerJobsCount } from 'composables/useCareerJobsCountCache.js'
import FlashNotifications from 'controllers/vue_components/flash_toast'
import CareerJobFormSlideOver from 'controllers/vue_components/admin/career_jobs/CareerJobFormSlideOver.vue'
import CareerJobApplicantsSlideOver from 'controllers/vue_components/admin/career_jobs/CareerJobApplicantsSlideOver.vue'

const STATUS_CLASSES = {
  draft: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400',
  published: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400',
  expired: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
}
const STATUS_DOTS = { draft: 'bg-gray-400', published: 'bg-emerald-500', expired: 'bg-amber-500' }
const STATUS_LABELS = { draft: 'Draft', published: 'Live', expired: 'Past deadline' }

const store = useAdminCareerJobsStore()
const flash = ref(null)

const records = computed(() => store.records)
const loading = computed(() => store.loading)
const error = computed(() => store.error)
const page = computed(() => store.page)
const status = computed(() => store.status)

const search = ref(store.search)
const working = ref(null)
const formOpen = ref(false)
const formError = ref('')
const saving = ref(false)
const editing = ref(null)
const pendingDelete = ref(null)
let deleteTimer = null

const applicantsOpen = ref(false)
const applicantsLoading = ref(false)
const applicantsError = ref('')
const applicantsJobTitle = ref('')
const applicants = ref([])

// Read synchronously so the first paint draws the right number of rows.
const skeletonRows = ref(getCachedCareerJobsCount())

const hasFilters = computed(() => !!store.search || !!store.status)

function countFor(value) {
  return value === '' ? store.counts.total : (store.counts[value] ?? 0)
}

function statusClass(value) { return STATUS_CLASSES[value] ?? STATUS_CLASSES.draft }
function statusDot(value) { return STATUS_DOTS[value] ?? STATUS_DOTS.draft }
function statusLabel(value) { return STATUS_LABELS[value] ?? 'Draft' }

function formatDate(value) {
  return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

let searchTimer = null
function onSearchInput() {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(async () => {
    store.setSearch(search.value)
    await store.fetchList()
  }, 300)
}

async function setStatus(value) {
  store.setStatus(value)
  await store.fetchList()
}

async function goToPage(number) {
  store.goToPage(number)
  await store.fetchList()
}

async function retry() {
  store.invalidate()
  await store.fetchIfStale()
}

function openNew() {
  editing.value = null
  formError.value = ''
  formOpen.value = true
}

// The list is rendered with the blueprint's :detail view, so every row already
// carries its description HTML and the panel opens with no round trip.
function openEdit(record) {
  formError.value = ''
  editing.value = record
  formOpen.value = true
}

// redaxios rejects on any non-2xx, so a render_failure comes back as
// `js_error` with the JSON body on `error.data`, not on `response`.
function errorMessage(res, fallback) {
  const body = res?.js_error ? res.error?.data : res?.response
  const details = body?.data
  if (Array.isArray(details) && details.length) return details.join(', ')
  return body?.message || fallback
}

async function onFormSubmit({ intent, attributes }) {
  saving.value = true
  formError.value = ''
  try {
    const id = editing.value?.id
    const res = id ? await store.update(id, attributes) : await store.create(attributes)
    if (res.js_error || !res.response.success) {
      formError.value = errorMessage(res, 'Could not save the role.')
      return
    }

    const saved = res.response.data
    if (intent === 'publish' && !saved.publish) {
      const publishRes = await store.publish(saved.id)
      if (publishRes.js_error || !publishRes.response.success) {
        formError.value = errorMessage(publishRes, 'Saved, but could not publish.')
        await store.fetchList()
        return
      }
    }

    formOpen.value = false
    await store.fetchList()
    flash.value?.show(intent === 'publish' ? `${saved.title} is live.` : `${saved.title} saved.`, 'notice')
  } finally {
    saving.value = false
  }
}

async function publish(record) {
  working.value = record.id
  const res = await store.publish(record.id)
  working.value = null
  if (res.js_error || !res.response.success) {
    flash.value?.show(errorMessage(res, 'Could not publish.'), 'alert')
    return
  }
  await store.fetchList()
  flash.value?.show(`${record.title} is live.`, 'notice')
}

async function unpublish(record) {
  working.value = record.id
  const res = await store.unpublish(record.id)
  working.value = null
  if (res.js_error || !res.response.success) {
    flash.value?.show(errorMessage(res, 'Could not unpublish.'), 'alert')
    return
  }
  await store.fetchList()
  flash.value?.show(`${record.title} is no longer listed.`, 'notice')
}

// Two step rather than a browser confirm(), which blocks the whole page.
// Only offered on a role with no applicants; the server refuses the rest.
function askDelete(record) {
  if (pendingDelete.value === record.id) {
    destroy(record)
    return
  }
  pendingDelete.value = record.id
  clearTimeout(deleteTimer)
  deleteTimer = setTimeout(() => { pendingDelete.value = null }, 4000)
}

async function destroy(record) {
  pendingDelete.value = null
  working.value = record.id
  const res = await store.destroy(record.id)
  working.value = null
  if (res.js_error || !res.response.success) {
    flash.value?.show(errorMessage(res, 'Could not delete.'), 'alert')
    return
  }
  await store.fetchList()
  flash.value?.show(`${record.title} deleted.`, 'notice')
}

async function openApplicants(record) {
  applicantsJobTitle.value = record.title
  applicants.value = []
  applicantsError.value = ''
  applicantsLoading.value = true
  applicantsOpen.value = true

  const res = await store.fetchApplicants(record.id)
  applicantsLoading.value = false
  if (res.js_error || !res.response.success) {
    applicantsError.value = errorMessage(res, 'Something went wrong.')
    return
  }
  applicants.value = res.response.data.records
}

onMounted(async () => {
  await store.fetchIfStale()
  if (!store.error) {
    skeletonRows.value = store.records.length
    setCachedCareerJobsCount(store.records.length)
  }
})
</script>
